import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AlbumShareResult from "./AlbumShareResult";

const PhotoTaggingFlow = ({ uploadedPhotos, onComplete }) => {
  const [selectedPhotos, setSelectedPhotos] = useState(new Set([0])); // Start with first photo selected
  const [tagInput, setTagInput] = useState("");
  const [taggedPeople, setTaggedPeople] = useState([]);
  const [photoTags, setPhotoTags] = useState({}); // Store tags for each photo
  const [albumName, setAlbumName] = useState("");
  const [location, setLocation] = useState("");
  const [showOnProfile, setShowOnProfile] = useState(false);
  const [isPublic, setIsPublic] = useState(
    localStorage.getItem("lastIsPublic") === "true"
  );
  const [friends, setFriends] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showTagTip, setShowTagTip] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showTagModal, setShowTagModal] = useState(false);
  const [selectedPhotoForTagView, setSelectedPhotoForTagView] = useState(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [taggedComplete, setTaggedComplete] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [uploadingMore, setUploadingMore] = useState(false);
  const [uploadMoreProgress, setUploadMoreProgress] = useState(0);
  const fileInputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    // Initialize photoTags for all photos
    const initialTags = {};
    uploadedPhotos.forEach((photo, index) => {
      initialTags[index] = [];
    });
    setPhotoTags(initialTags);

    // Get album details from uploaded photos
    const currentPhoto = uploadedPhotos[0];
    if (currentPhoto) {
      if (currentPhoto.albumName) setAlbumName(currentPhoto.albumName);
      if (currentPhoto.location) setLocation(currentPhoto.location);
      if (currentPhoto.showOnProfile !== undefined) {
        setShowOnProfile(currentPhoto.showOnProfile);
      }
    }

    // Fetch friends for tagging
    fetchFriends();
  }, [uploadedPhotos]);

  useEffect(() => {
    // Calculate progress based on tagged photos
    const taggedPhotosCount = Object.values(photoTags).filter(
      (tags) => tags.length > 0
    ).length;
    setProgress((taggedPhotosCount / uploadedPhotos.length) * 100);
  }, [photoTags, uploadedPhotos.length]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isSearching && searchQuery.trim()) {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        setIsLoading(true);
        try {
          const { data } = await axios.get(
            `${
              process.env.REACT_APP_DATABASE_URL
            }/api/users/search?q=${encodeURIComponent(searchQuery)}`
          );
          const filtered = data.filter(
            (u) =>
              !friends.some((f) => f.id === u.id) &&
              !taggedPeople.some((p) => p.id === u.id)
          );
          setSearchResults(filtered);
          setDropdownOpen(filtered.length > 0);
        } catch {
          console.error("Error searching users");
        } finally {
          setIsLoading(false);
        }
      }, 300);
    } else if (!isSearching) {
      // tag‑mode local filter
      const val = tagInput.trim().toLowerCase();
      if (!val) {
        setSearchResults([]);
        setDropdownOpen(false);
      } else {
        const exact = friends.some((f) => f.name.toLowerCase() === val);

        const matches = friends
          .filter((f) => f.name.toLowerCase().includes(val))
          .filter((f) => !taggedPeople.some((p) => p.id === f.id));
        setSearchResults(matches);
        setDropdownOpen(matches.length > 0);
      }
    }
  }, [searchQuery, tagInput, isSearching]);

  // Fetch user's friends
  const fetchFriends = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_DATABASE_URL}/api/users/friends`
      );
      setFriends(response.data);
    } catch (error) {
      console.error("Error fetching friends:", error);
    }
  };

  // Handle search for friends
  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = friends.filter(
        (friend) =>
          friend.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !taggedPeople.some((person) => person.id === friend.id)
      );
      setSearchResults(filtered);
      setDropdownOpen(filtered.length > 0);
    } else {
      setSearchResults([]);
      setDropdownOpen(false);
    }
  }, [searchQuery, friends, taggedPeople]);

  const handlePhotoSelection = (photoIndex) => {
    const newSelected = new Set(selectedPhotos);
    if (newSelected.has(photoIndex)) {
      newSelected.delete(photoIndex);
    } else {
      newSelected.add(photoIndex);
    }
    setSelectedPhotos(newSelected);
  };

  const selectAllPhotos = () => {
    const allIndices = uploadedPhotos.map((_, index) => index);
    setSelectedPhotos(new Set(allIndices));
  };

  const deselectAllPhotos = () => {
    setSelectedPhotos(new Set());
  };
  const handleButtonClick = () => {
    if (isSearching) {
      // if you’re debouncing, force immediate search:
      clearTimeout(debounceRef.current);
      handleSearchFriend();
    } else {
      handleAddTag();
    }
  };
  const handleAddTag = () => {
    if (!tagInput.trim()) return;

    // Check if the user is a friend
    const friend = friends.find(
      (friend) => friend.name.toLowerCase() === tagInput.toLowerCase()
    );

    if (friend) {
      // Check if already tagged
      const alreadyTagged = taggedPeople.some(
        (person) => person.id === friend.id
      );

      if (!alreadyTagged) {
        // Add friend as a tag
        setTaggedPeople([
          ...taggedPeople,
          {
            id: friend.id,
            name: friend.name,
            username: friend.username,
            isExisting: true,
            profilePhotoUrl: friend.profilePhotoUrl,
          },
        ]);
      }
      setTagInput("");
      setDropdownOpen(false);
    } else {
      // Not in friends list - show add friend option
      setIsSearching(true);
      setSearchQuery(tagInput);
    }
  };

  const handleSearchFriend = async () => {
    if (!searchQuery.trim()) return;

    setIsLoading(true);
    try {
      const response = await axios.get(
        `${
          process.env.REACT_APP_DATABASE_URL
        }/api/users/search?q=${encodeURIComponent(searchQuery)}`
      );

      // Filter out existing friends and already tagged people
      const filteredResults = response.data.filter(
        (user) =>
          !friends.some((friend) => friend.id === user.id) &&
          !taggedPeople.some((person) => person.id === user.id)
      );

      setSearchResults(filteredResults);
      setDropdownOpen(filteredResults.length > 0);
    } catch (error) {
      console.error("Error searching users:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendFriendRequest = async (userId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_DATABASE_URL}/api/users/friends/request`,
        { userId }
      );

      // Update the UI to show pending request
      setSearchResults((prev) =>
        prev.map((user) =>
          user.id === userId ? { ...user, requestSent: true } : user
        )
      );
    } catch (error) {
      console.error("Error sending friend request:", error);
    }
  };

  const handleTagPhotos = () => {
    if (selectedPhotos.size === 0 || taggedPeople.length === 0) return;

    // Add the current tagged people to all selected photos
    const newPhotoTags = { ...photoTags };
    selectedPhotos.forEach((photoIndex) => {
      const existingTags = newPhotoTags[photoIndex] || [];
      const newTags = taggedPeople.filter(
        (person) => !existingTags.some((tag) => tag.id === person.id)
      );
      newPhotoTags[photoIndex] = [...existingTags, ...newTags];
    });

    setPhotoTags(newPhotoTags);
    setTaggedPeople([]); // Clear the current tagging list
    setTagInput("");
    setSearchQuery("");
    setDropdownOpen(false);
  };

  const removeTagFromPhoto = (photoIndex, tagId) => {
    const newPhotoTags = { ...photoTags };
    newPhotoTags[photoIndex] = newPhotoTags[photoIndex].filter(
      (tag) => tag.id !== tagId
    );
    setPhotoTags(newPhotoTags);
  };

  const handleCompleteTagging = async () => {
    setSaveLoading(true);

    try {
      // Save tags for all photos
      for (let i = 0; i < uploadedPhotos.length; i++) {
        const tags = photoTags[i] || [];
        await axios.post(
          `${process.env.REACT_APP_DATABASE_URL}/api/photos/${uploadedPhotos[i].id}/tags`,
          {
            tags: tags.map((person) => ({
              userId: person.id,
              name: person.name,
              username: person.username, // Add username
              isExisting: person.isExisting,
              email: person.email,
            })),
            albumName,
            location,
            showOnProfile,
            isPublic,
          }
        );
      }

      // Check if the album should be public
      if (isPublic) {
        // Generate share link
        const uploadBatch = uploadedPhotos[0].uploadBatch;
        const response = await axios.post(
          `${process.env.REACT_APP_DATABASE_URL}/api/photos/share`,
          { albumId: uploadBatch }
        );

        setShareUrl(response.data.shareUrl);
      }

      setTaggedComplete(true);
    } catch (error) {
      console.error("Error completing tagging:", error);
    } finally {
      setSaveLoading(false);
    }
  };

  const getPhotoFileName = (index) => {
    const photo = uploadedPhotos[index];
    return photo?.filename || `Photo ${index + 1}`;
  };

  const clearSearch = () => {
    setTagInput("");
    setSearchQuery("");
    setIsSearching(false);
    setDropdownOpen(false);
    inputRef.current.focus();
  };

  const handleUploadMore = () => {
    fileInputRef.current?.click();
  };

  const processMoreUploads = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    // Filter for image files
    const validFiles = files.filter((file) => file.type.startsWith("image/"));

    if (validFiles.length === 0) {
      alert("Please select valid image files");
      return;
    }

    setUploadingMore(true);
    setUploadMoreProgress(0);

    try {
      const formData = new FormData();
      validFiles.forEach((file) => {
        formData.append("photos", file);
      });

      // Add album details from current photos
      formData.append("albumName", albumName);
      formData.append("location", location);
      formData.append("showOnProfile", showOnProfile.toString());
      formData.append("uploadBatch", uploadedPhotos[0].uploadBatch);

      const response = await axios.post(
        `${process.env.REACT_APP_DATABASE_URL}/api/photos/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadMoreProgress(percentCompleted);
          },
        }
      );

      // Add the new photos to the existing photos array
      const newPhotos = response.data.photos;

      // Update photoTags to include the new photos
      const updatedPhotoTags = { ...photoTags };
      newPhotos.forEach((_, index) => {
        updatedPhotoTags[uploadedPhotos.length + index] = [];
      });
      setPhotoTags(updatedPhotoTags);

      // Update the uploaded photos array
      const updatedPhotos = [...uploadedPhotos, ...newPhotos];
      uploadedPhotos.splice(0, uploadedPhotos.length, ...updatedPhotos);

      // Recalculate progress
      const taggedCount = Object.values(updatedPhotoTags).filter(
        (tags) => tags.length > 0
      ).length;
      setProgress((taggedCount / updatedPhotos.length) * 100);
    } catch (error) {
      console.error("Error uploading additional photos:", error);
      alert("Failed to upload additional photos. Please try again.");
    } finally {
      setUploadingMore(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Count photos with tags
  const taggedPhotosCount = Object.values(photoTags).filter(
    (tags) => tags.length > 0
  ).length;

  if (taggedComplete) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Tagging Complete!
          </h2>
          <p className="mt-2 text-gray-600">
            Your photos have been uploaded and tagged successfully
          </p>
        </div>

        {shareUrl && <AlbumShareResult shareUrl={shareUrl} />}

        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="mb-6">
            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-10 w-10 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>

          <p className="text-lg text-gray-800 mb-8">
            {shareUrl
              ? "Your photos are now available publicly via the link above. You can also view them in your feed."
              : "Your photos have been uploaded and can be viewed in your feed."}
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => navigate("/feed")}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-pink-700 shadow-md"
            >
              Go to My Photos
            </button>

            <button
              onClick={() => navigate("/upload")}
              className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200"
            >
              Upload More Photos
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 pt-6">
      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-2xl font-bold text-gray-900">
            Tag People In Your Photos
          </h2>
          <span className="text-sm text-gray-600">
            {taggedPhotosCount} of {uploadedPhotos.length} photos tagged
          </span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-purple-600 to-pink-600 transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      {/* Album and Location */}
      <div className="bg-white p-6 rounded-xl shadow-lg mb-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Album Details</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
          <div>
            <label
              className="block text-sm font-medium text-gray-700 mb-1"
              htmlFor="albumName"
            >
              Album Name
            </label>
            <input
              id="albumName"
              type="text"
              value={albumName}
              onChange={(e) => setAlbumName(e.target.value)}
              placeholder="e.g., Summer Vacation, Birthday Party"
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
            />
          </div>

          <div>
            <label
              className="block text-sm font-medium text-gray-700 mb-1"
              htmlFor="location"
            >
              Location
            </label>
            <input
              id="location"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g., Miami Beach, Central Park"
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={showOnProfile}
              onChange={(e) => setShowOnProfile(e.target.checked)}
              className="h-5 w-5 text-purple-600 rounded focus:ring-purple-500 border-gray-300"
            />
            <span className="ml-2 text-gray-700">
              Show on Profile (Visible to everyone, including non-friends)
            </span>
          </label>
          <p className="mt-1 text-sm text-gray-500 ml-7">
            If unchecked, photos will only be visible to friends you tag
          </p>
        </div>

        <div className="mt-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => {
                setIsPublic(e.target.checked);
                localStorage.setItem("lastIsPublic", e.target.checked);
              }}
              className="h-5 w-5 text-purple-600 rounded focus:ring-purple-500 border-gray-300"
            />
            <span className="ml-2 text-gray-700">
              Make album public (Anyone with the link can view these photos)
            </span>
          </label>
          <p className="mt-1 text-sm text-gray-500 ml-7">
            You'll get a shareable link after completing the tagging process
          </p>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={processMoreUploads}
        multiple
        accept="image/*"
        className="hidden"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Photo Selection Grid */}
        <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-gray-900">
              Select Photos to Tag ({selectedPhotos.size} selected)
            </h3>
            <div className="flex gap-2">
              <button
                onClick={selectAllPhotos}
                className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200"
              >
                Select All
              </button>
              <button
                onClick={deselectAllPhotos}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Deselect All
              </button>
              <button
                onClick={handleUploadMore}
                disabled={uploadingMore}
                className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200 disabled:opacity-50 flex items-center"
              >
                {uploadingMore ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4 mr-1"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Uploading...
                  </>
                ) : (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Upload More
                  </>
                )}
              </button>
              {/* <button
                // onClick={handleDeletePhotos}
                disabled={selectedPhotos.size === 0}
                className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 disabled:opacity-50 flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                Delete Selected
              </button> */}
            </div>
          </div>

          {/* Upload progress indicator */}
          {uploadingMore && (
            <div className="mb-4">
              <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadMoreProgress}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-500 text-right">
                Uploading additional photos: {uploadMoreProgress}%
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {uploadedPhotos.map((photo, index) => (
              <div key={index} className="relative group">
                <div
                  className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all
                    ${
                      selectedPhotos.has(index)
                        ? "border-purple-500 shadow-lg"
                        : "border-gray-200 hover:border-purple-300"
                    }`}
                  onClick={() => handlePhotoSelection(index)}
                >
                  <div className="aspect-square">
                    <img
                      src={photo.url}
                      alt={getPhotoFileName(index)}
                      className="w-full h-full object-cover"
                    />
                    {/* Selection indicator - needs higher z-index */}
                    <div
                      className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-all z-10
                      ${
                        selectedPhotos.has(index)
                          ? "bg-purple-600 scale-100"
                          : "bg-white/80 scale-0 group-hover:scale-100"
                      }`}
                    >
                      {selectedPhotos.has(index) && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-white"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>

                    {/* Tags overlay on photo - show limited tags with "more" indicator */}
                    {photoTags[index] && photoTags[index].length > 0 && (
                      <div className="absolute inset-0  opacity-100">
                        {/* Tag info at the bottom only */}
                        <div className="absolute bottom-0 left-0 right-0 p-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // This prevents the click from reaching the parent
                              setSelectedPhotoForTagView(index);
                              setShowTagModal(true);
                            }}
                            className="w-full flex flex-wrap gap-1 max-w-full p-1 rounded-t-md"
                          >
                            {/* Show first 2-3 tags */}
                            {photoTags[index]
                              .slice(0, 2)
                              .map((tag, tagIndex) => (
                                <div
                                  key={tagIndex}
                                  className="bg-purple-100 text-xs px-2 py-1 rounded-full text-purple-700"
                                >
                                  {tag.name}
                                </div>
                              ))}

                            {/* Show "more" indicator if there are additional tags */}
                            {photoTags[index].length > 2 && (
                              <div className="bg-purple-500/90 text-white text-xs px-2 py-1 rounded-full">
                                +{photoTags[index].length - 2} more
                              </div>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <p className="mt-1 text-xs text-gray-500 text-center truncate">
                    {getPhotoFileName(index)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tagging interface */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2 text-purple-600"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
            Tag People
          </h3>

          {/* Tag tip alert */}
          {showTagTip && (
            <div className="mb-4 p-3 bg-blue-50 text-blue-800 rounded-lg border border-blue-200 flex">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm">
                  Select photos and add friends to tag them all at once!
                </p>
              </div>
              <button
                onClick={() => setShowTagTip(false)}
                className="ml-2 text-blue-700 hover:text-blue-900"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          )}

          {/* Search or tag friends */}
          <div className="relative mb-6" ref={dropdownRef}>
            <div className="flex">
              <div className="relative flex-grow">
                <input
                  ref={inputRef}
                  type="text"
                  value={isSearching ? searchQuery : tagInput}
                  onChange={(e) => {
                    isSearching
                      ? setSearchQuery(e.target.value)
                      : setTagInput(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (isSearching) {
                        /* select first or do nothing */
                      } else {
                        handleAddTag(tagInput);
                      }
                    }
                  }}
                  placeholder={
                    isSearching
                      ? "Search for people to add as friends…"
                      : "Enter name of a friend"
                  }
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />

                {(isSearching || tagInput) && (
                  <button
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={clearSearch}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                )}

                {/* Autocomplete dropdown */}
                {dropdownOpen && searchResults.length > 0 && (
                  <ul className="absolute z-10 w-full bg-white border rounded-lg mt-1 shadow-lg max-h-60 overflow-auto">
                    {searchResults.map((user) => (
                      <li key={user.id} className="border-b last:border-0">
                        <div className="px-4 py-3 hover:bg-gray-50 flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="h-8 w-8 rounded-full bg-purple-200 flex items-center justify-center overflow-hidden mr-3">
                              {user.profilePhotoUrl ? (
                                <img
                                  src={user.profilePhotoUrl}
                                  alt={user.name}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <span className="text-sm font-medium text-purple-700">
                                  {user.name.charAt(0).toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {user.name}
                              </p>
                              {user.username && (
                                <p className="text-xs text-gray-500">
                                  @{user.username}
                                </p>
                              )}
                            </div>
                          </div>

                          {isSearching ? (
                            // Add friend button
                            user.isFriend ? (
                              <button
                                onClick={() => {
                                  setTaggedPeople([
                                    ...taggedPeople,
                                    {
                                      id: user.id,
                                      name: user.name,
                                      username: user.username,
                                      isExisting: true,
                                      profilePhotoUrl: user.profilePhotoUrl,
                                    },
                                  ]);
                                  setTagInput("");
                                  setDropdownOpen(false);
                                }}
                                className="px-3 py-1 bg-purple-600 text-white text-xs rounded-full hover:bg-purple-700"
                              >
                                Add
                              </button>
                            ) : user.requestSent ? (
                              <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                Request Sent
                              </span>
                            ) : (
                              <button
                                onClick={() => handleSendFriendRequest(user.id)}
                                className="px-3 py-1 bg-purple-600 text-white text-xs rounded-full hover:bg-purple-700"
                              >
                                Add Friend
                              </button>
                            )
                          ) : (
                            // Tag friend button
                            <button
                              onClick={() => {
                                setTaggedPeople([
                                  ...taggedPeople,
                                  {
                                    id: user.id,
                                    name: user.name,
                                    isExisting: true,
                                    profilePhotoUrl: user.profilePhotoUrl,
                                  },
                                ]);
                                setTagInput("");
                                setDropdownOpen(false);
                              }}
                              className="px-3 py-1 bg-purple-600 text-white text-xs rounded-full hover:bg-purple-700"
                            >
                              Add
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Mode switch */}
            <div className="mt-2 flex justify-end">
              <button
                onClick={() => {
                  setIsSearching(!isSearching);
                  setTagInput("");
                  setSearchQuery("");
                  setDropdownOpen(false);
                }}
                className="text-sm text-purple-600 hover:text-purple-800"
              >
                {isSearching
                  ? "Back to tagging friends"
                  : "Search for new friends"}
              </button>
            </div>
          </div>

          {/* Tagged people list */}
          <div className="mb-6">
            <h4 className="font-medium text-gray-900 mb-2 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2 text-green-600"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              Friends to Tag:{" "}
              {taggedPeople.length > 0 ? taggedPeople.length : "None yet"}
            </h4>

            {taggedPeople.length > 0 ? (
              <ul className="space-y-2 mt-3">
                {taggedPeople.map((person, index) => (
                  <li
                    key={index}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-purple-200 flex items-center justify-center overflow-hidden mr-3">
                        {person.profilePhotoUrl ? (
                          <img
                            src={person.profilePhotoUrl}
                            alt={person.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <span className="text-sm font-medium text-purple-700">
                            {person.name.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div>
                        <span className="font-medium">{person.name}</span>
                        {person.username && (
                          <div className="text-xs text-gray-500">
                            @{person.username}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const updatedTags = [...taggedPeople];
                        updatedTags.splice(index, 1);
                        setTaggedPeople(updatedTags);
                      }}
                      className="text-gray-400 hover:text-red-600 p-1"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="mx-auto h-10 w-10 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
                <p className="mt-2 text-sm text-gray-500">Add friends to tag</p>
              </div>
            )}
          </div>

          {/* Tag selected photos button */}
          <button
            onClick={handleTagPhotos}
            disabled={selectedPhotos.size === 0 || taggedPeople.length === 0}
            className="w-full px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-medium rounded-lg shadow-md hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center mb-4"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
              />
            </svg>
            Tag {selectedPhotos.size} Photo
            {selectedPhotos.size !== 1 ? "s" : ""} with {taggedPeople.length}{" "}
            Friend{taggedPeople.length !== 1 ? "s" : ""}
          </button>

          {/* Complete tagging button */}
          <button
            onClick={handleCompleteTagging}
            disabled={saveLoading}
            className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-lg shadow-md hover:from-purple-700 hover:to-pink-700 disabled:opacity-70 transition-all flex items-center justify-center"
          >
            {saveLoading ? (
              <div className="flex items-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Saving...
              </div>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Complete & View Photos
              </>
            )}
          </button>

          {/* Summary */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Summary</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <div className="flex justify-between">
                <span>Total photos:</span>
                <span className="font-medium">{uploadedPhotos.length}</span>
              </div>
              <div className="flex justify-between">
                <span>Photos with tags:</span>
                <span className="font-medium text-green-600">
                  {taggedPhotosCount}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Photos without tags:</span>
                <span className="font-medium text-orange-600">
                  {uploadedPhotos.length - taggedPhotosCount}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tag Management Modal */}
      {showTagModal && selectedPhotoForTagView !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-900">
                  Tagged People
                </h3>
                <button
                  onClick={() => {
                    setShowTagModal(false);
                    setSelectedPhotoForTagView(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {getPhotoFileName(selectedPhotoForTagView)}
              </p>
            </div>

            <div className="p-4 max-h-96 overflow-y-auto">
              {photoTags[selectedPhotoForTagView] &&
              photoTags[selectedPhotoForTagView].length > 0 ? (
                <div className="space-y-3">
                  {photoTags[selectedPhotoForTagView].map((tag, tagIndex) => (
                    <div
                      key={tagIndex}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-purple-200 flex items-center justify-center overflow-hidden mr-3">
                          {tag.profilePhotoUrl ? (
                            <img
                              src={tag.profilePhotoUrl}
                              alt={tag.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <span className="text-sm font-medium text-purple-700">
                              {tag.name.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {tag.name}
                          </p>
                          {tag.username ? (
                            <p className="text-xs text-gray-500">
                              @{tag.username}
                            </p>
                          ) : (
                            <p className="text-xs text-gray-500">Tagged</p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          removeTagFromPhoto(selectedPhotoForTagView, tag.id);
                          // Close modal if no tags left
                          if (photoTags[selectedPhotoForTagView].length === 1) {
                            setShowTagModal(false);
                            setSelectedPhotoForTagView(null);
                          }
                        }}
                        className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500">
                    No one is tagged in this photo
                  </p>
                </div>
              )}
            </div>

            <div className="p-4 border-t">
              <button
                onClick={() => {
                  setShowTagModal(false);
                  setSelectedPhotoForTagView(null);
                }}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoTaggingFlow;
